import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function NewApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [embassies, setEmbassies] = useState([]);
  const [form, setForm] = useState({
    full_name: user?.name || '',
    date_of_birth: '',
    place_of_birth: '',
    gender: '',
    nationality: 'Congolaise',
    passport_number: '',
    country_of_residence: user?.country_of_residence || '',
    city_of_residence: '',
    address: '',
    phone: '',
    email: user?.email || '',
    profession: '',
  });

  useEffect(() => {
    api.get('/embassies').then(r => setEmbassies(r.data)).catch(() => {});
  }, []);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await api.post('/applications', form);
      toast.success('Dossier cree ! Ajoutez vos documents puis soumettez.');
      navigate('/citizen/documents', { state: { applicationId: resp.data.application_id } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="new-application-page">
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Nouveau dossier</CardTitle>
          <CardDescription>Remplissez les informations de votre dossier</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom complet *</Label>
                <Input value={form.full_name} onChange={e => updateField('full_name', e.target.value)} required data-testid="app-full-name" />
              </div>
              <div className="space-y-2">
                <Label>Date de naissance *</Label>
                <Input type="date" value={form.date_of_birth} onChange={e => updateField('date_of_birth', e.target.value)} required data-testid="app-dob" />
              </div>
              <div className="space-y-2">
                <Label>Lieu de naissance *</Label>
                <Input value={form.place_of_birth} onChange={e => updateField('place_of_birth', e.target.value)} required data-testid="app-pob" />
              </div>
              <div className="space-y-2">
                <Label>Genre *</Label>
                <Select value={form.gender} onValueChange={v => updateField('gender', v)}>
                  <SelectTrigger data-testid="app-gender"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculin</SelectItem>
                    <SelectItem value="F">Feminin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nationalite</Label>
                <Input value={form.nationality} onChange={e => updateField('nationality', e.target.value)} data-testid="app-nationality" />
              </div>
              <div className="space-y-2">
                <Label>N de Passeport *</Label>
                <Input value={form.passport_number} onChange={e => updateField('passport_number', e.target.value)} required data-testid="app-passport" />
              </div>
              <div className="space-y-2">
                <Label>Pays de residence *</Label>
                <Select value={form.country_of_residence} onValueChange={v => updateField('country_of_residence', v)}>
                  <SelectTrigger data-testid="app-country"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {embassies.map(e => <SelectItem key={e.embassy_id} value={e.country}>{e.country}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ville de residence *</Label>
                <Input value={form.city_of_residence} onChange={e => updateField('city_of_residence', e.target.value)} required data-testid="app-city" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Adresse *</Label>
                <Input value={form.address} onChange={e => updateField('address', e.target.value)} required data-testid="app-address" />
              </div>
              <div className="space-y-2">
                <Label>Telephone *</Label>
                <Input value={form.phone} onChange={e => updateField('phone', e.target.value)} required data-testid="app-phone" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} data-testid="app-email" />
              </div>
              <div className="space-y-2">
                <Label>Profession</Label>
                <Input value={form.profession} onChange={e => updateField('profession', e.target.value)} data-testid="app-profession" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="app-cancel-btn">Annuler</Button>
              <Button type="submit" className="bg-[#007FFF] hover:bg-[#0066CC]" disabled={loading} data-testid="app-submit-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Creer le dossier
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
